import { useState, useEffect } from "react";
import { BUILTIN_PHOTOS, type GalleryPhoto } from "@/pages/admin/GalleryPage";
import { loadUploadedPhotosAsync } from "@/utils/galleryStore";

export type { GalleryPhoto };
export const SITE_GALLERY_PHOTOS = BUILTIN_PHOTOS;

const DELETED_BUILTINS_KEY = "ma_gallery_deleted_builtins";

interface SiteImageGalleryProps {
  isDark: boolean;
  onSelect: (url: string) => void;
  onClose: () => void;
  title?: string;
}

export default function SiteImageGallery({
  isDark,
  onSelect,
  onClose,
  title = "Choose an Image",
}: SiteImageGalleryProps) {
  const [filter, setFilter] = useState<"all" | "hero">("all");
  const [customUrl, setCustomUrl] = useState("");
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [allPhotos, setAllPhotos] = useState<GalleryPhoto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let deleted = new Set<string>();
    try {
      const raw = localStorage.getItem(DELETED_BUILTINS_KEY);
      if (raw) deleted = new Set(JSON.parse(raw));
    } catch { /* ignore */ }

    loadUploadedPhotosAsync().then((uploaded) => {
      const builtins = BUILTIN_PHOTOS.filter((p) => !deleted.has(p.id));
      setAllPhotos([...builtins, ...uploaded]);
      setLoading(false);
    });
  }, []);

  const filtered = filter === "all" ? allPhotos : allPhotos.filter((p) => p.isHero);

  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const bg = isDark ? "bg-gray-900" : "bg-white";
  const borderColor = isDark ? "border-gray-700" : "border-gray-200";
  const inputCls = isDark
    ? "flex-1 border border-gray-600 bg-gray-800 text-white placeholder-gray-500 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
    : "flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200";

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/75 p-3"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-7xl flex flex-col rounded-3xl border overflow-hidden shadow-2xl ${bg} ${borderColor}`}
        style={{ height: "92vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-8 py-5 border-b flex-shrink-0 ${borderColor}`}>
          <div>
            <h2 className={`font-bold text-xl ${headingColor}`}>{title}</h2>
            <p className={`text-sm mt-0.5 ${subColor}`}>
              {loading ? "Loading..." : `${filtered.length} photos — click any to use it`}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Custom URL bar */}
        <div className={`px-8 py-4 border-b flex-shrink-0 ${borderColor} ${isDark ? "bg-gray-800/50" : "bg-gray-50"}`}>
          <div className="flex gap-3 items-center">
            <i className={`ri-link text-base ${subColor}`}></i>
            <input
              type="text"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && customUrl.trim()) {
                  onSelect(customUrl.trim());
                  onClose();
                }
              }}
              placeholder="Or paste your own image URL here and press Enter..."
              className={inputCls}
            />
            <button
              onClick={() => { if (customUrl.trim()) { onSelect(customUrl.trim()); onClose(); } }}
              disabled={!customUrl.trim()}
              className="bg-pink-500 hover:bg-pink-600 disabled:opacity-40 text-white text-sm font-bold px-5 py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors"
            >
              Use URL
            </button>
          </div>
        </div>

        {/* Filter tabs */}
        <div className={`px-8 py-3 border-b flex-shrink-0 ${borderColor}`}>
          <div className="flex gap-2">
            {[
              { id: "all" as const, label: `All Photos (${allPhotos.length})` },
              { id: "hero" as const, label: `Hero / Full Width (${allPhotos.filter((p) => p.isHero).length})` },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                  filter === tab.id
                    ? "bg-pink-500 text-white"
                    : isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Photo grid */}
        <div className="flex-1 overflow-y-auto p-8">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <i className="ri-loader-4-line animate-spin text-3xl text-pink-400"></i>
                <p className={`text-sm mt-3 ${subColor}`}>Loading gallery...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {filtered.map((photo) => (
                <button
                  key={photo.id}
                  onClick={() => { onSelect(photo.url); onClose(); }}
                  onMouseEnter={() => setHoveredId(photo.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className={`relative group rounded-2xl overflow-hidden border-2 cursor-pointer transition-all text-left ${
                    hoveredId === photo.id
                      ? "border-pink-500 scale-[1.02]"
                      : isDark ? "border-gray-700 hover:border-pink-400" : "border-gray-200 hover:border-pink-300"
                  }`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={photo.thumb}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-pink-500 text-white text-sm font-bold px-5 py-2 rounded-full shadow-lg">
                      Use This
                    </div>
                  </div>
                  {photo.isHero && (
                    <div className="absolute top-2 left-2 bg-black/50 text-white text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm">
                      Hero
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
                <i className="ri-image-line text-gray-400 text-2xl"></i>
              </div>
              <p className={`text-base font-semibold mb-1 ${headingColor}`}>No photos match this filter</p>
              <p className={`text-sm ${subColor}`}>Try switching to "All Photos" or upload new images in the Gallery admin page.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
